/**
 * CLI Parser Module
 *
 * Routes CLI commands to the appropriate parser based on the command prefix.
 * Supports Claude Code, Gemini CLI, and Codex CLI commands.
 */

import { claudeParser, codexParser, geminiParser } from './parsers';
import { tokenize } from './tokenizer';
import type { CliParseResult, CliTool, ICliParser } from './types';

/**
 * Registry of all available CLI parsers
 */
const parsers: ICliParser[] = [claudeParser, geminiParser, codexParser];

/**
 * Get parser for a specific CLI tool
 */
export const getParser = (tool: CliTool): ICliParser | undefined => {
  return parsers.find((p) => p.tool === tool);
};

/**
 * Parse a CLI command string into MCP server configuration(s)
 *
 * @param command - The CLI command to parse
 * @param tool - Optional tool hint (if already detected)
 * @returns Parse result with server configs or error
 *
 * @example
 * // Parse Claude command
 * const result = parseCliCommand('claude mcp add --transport http myserver https://example.com');
 * // => { success: true, servers: [{ name: 'myserver', config: {...} }] }
 *
 * @example
 * // Parse with tool hint
 * const result = parseCliCommand('mcp add myserver https://example.com', 'claude');
 */
export const parseCliCommand = (command: string, tool?: CliTool): CliParseResult => {
  // Tokenize the command
  const { tokens, warnings: tokenWarnings } = tokenize(command);

  if (tokens.length === 0) {
    return {
      success: false,
      servers: [],
      error: 'Empty command',
    };
  }

  // If tool is provided, use that parser directly
  if (tool) {
    const parser = getParser(tool);
    if (!parser) {
      return {
        success: false,
        servers: [],
        error: `Unknown CLI tool: ${tool}`,
      };
    }

    const result = parser.parse({ tokens, rawCommand: command });

    // Add tokenizer warnings
    if (tokenWarnings.length > 0) {
      result.warnings = [...(result.warnings || []), ...tokenWarnings];
    }

    return result;
  }

  // Try each parser to see which can handle it
  const parser = parsers.find((p) => p.canHandle(tokens));
  if (parser) {
    const result = parser.parse({ tokens, rawCommand: command });

    // Add tokenizer warnings
    if (tokenWarnings.length > 0) {
      result.warnings = [...(result.warnings || []), ...tokenWarnings];
    }

    return result;
  }

  // No parser matched
  return {
    success: false,
    servers: [],
    error:
      'Unrecognized CLI command. Expected command starting with "claude mcp add", "gemini mcp add", or "codex mcp add".',
  };
};
